import {
	CorporateAllocationData,
	CorporateDataControllerApiFactory,
	CorporateDataDto,
	CorporateDataResponse,
	CorporateYearData,
} from '@api-client/api'
import { QueryKey } from '@helpers/enums'
import { useDataFederationApiUrl } from '@hooks/useApi'
import { useAsyncApi } from '@hooks/useAsyncApi'
import { useOwnOrganisation } from '@hooks/useOwnOrganisation'
import authService from '@services/authService'
import {
	useMutation,
	useQuery,
	useQueryClient,
	UseQueryOptions,
} from '@tanstack/react-query'
import React from 'react'

export const useCorporateData = (
	reportingYear?: string,
	options: Omit<
		UseQueryOptions<readonly CorporateDataResponse[]>,
		'queryKey'
	> = {
		enabled: true,
	}
) => {
	const corporateDataApi = useAsyncApi(CorporateDataControllerApiFactory)
	const { data: organisationData } = useOwnOrganisation()
	const organisationId = organisationData?.data?.id

	const {
		data: corporateData,
		isLoading,
		isError,
		error,
	} = useQuery({
		queryKey: [QueryKey.GetCorporateData, organisationId],
		queryFn: () =>
			corporateDataApi(async api => {
				const response = await api.getCorporateData()
				return response.data ? [...response.data] : []
			}),
		staleTime: 5 * 60 * 1000, // 5 minutes
		refetchOnMount: 'always', // Always refetch on component mount to ensure fresh data
		enabled: !!organisationId && (options.enabled ?? true),
		...options,
	})

	const queryClient = useQueryClient()

	const saveMutation = useMutation({
		mutationFn: async ({
			reportingYear,
			data,
		}: {
			reportingYear: string
			data: CorporateYearData
		}) => {
			const corporateDataDto: CorporateDataDto = {
				year: Number.parseInt(reportingYear, 10),
				data,
			}

			const result = await corporateDataApi(api =>
				api.upsertCorporateData(corporateDataDto)
			)

			// Optimistically update the cache with the new data
			const year = Number.parseInt(reportingYear, 10)
			queryClient.setQueryData<CorporateDataResponse[]>(
				[QueryKey.GetCorporateData, organisationId],
				oldData => {
					if (!oldData) {
						return oldData
					}

					// Update existing year's data while preserving all other fields
					return oldData.map(item => {
						if (item.year === year) {
							return { ...item, data }
						}
						return item
					})
				}
			)

			return result
		},
	})

	const { mutateAsync: saveCorporateData, ...saveState } = saveMutation

	return {
		corporateData,
		isLoading,
		isError,
		error,
		saveCorporateData,
		isSaving: saveState.isPending,
	}
}

export const useGetCorporateDataRequests = (received: boolean) => {
	const corporateDataApi = useAsyncApi(CorporateDataControllerApiFactory)
	const { data: organisationData } = useOwnOrganisation()
	const organisationId = organisationData?.data?.id

	const { data, isLoading, isError, error } = useQuery({
		queryKey: [QueryKey.GetCorporateDataRequests, organisationId, received],
		queryFn: () =>
			corporateDataApi(api =>
				api.getCorporateDataRequests(
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					received
				)
			),
		enabled: !!organisationId,
	})

	return {
		corporateDataRequests: data?.data?.data,
		isLoading,
		isError,
		error,
	}
}

export const useIsCorporateDataEnabled = () => {
	const { data: organisationData } = useOwnOrganisation()
	const organisationId = organisationData?.data?.id
	const corporateDataApi = useAsyncApi(CorporateDataControllerApiFactory)

	const { data: corporateDataRequests } = useQuery({
		queryKey: [QueryKey.GetCorporateDataRequests, organisationId],
		queryFn: () =>
			corporateDataApi(api =>
				api.getCorporateDataRequests(
					0,
					1,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					true
				)
			),
		enabled: !!organisationId,
	})

	const isCorporateDataEnabled =
		organisationData?.data?.featureFlags?.hasCorporateRequestsEnabled ||
		(corporateDataRequests?.data.metadata.count ?? 0) > 0

	return {
		isCorporateDataEnabled,
	}
}

export const useGetCorporateDataRequestDetail = (reference?: string | null) => {
	const corporateDataApi = useAsyncApi(CorporateDataControllerApiFactory)

	const { data, isLoading, isError, error } = useQuery({
		queryKey: [QueryKey.GetCorporateDataRequest, reference],
		queryFn: () =>
			corporateDataApi(api =>
				api.getCorporateDataRequestDetailByReference(reference as string)
			),
		enabled: !!reference,
	})

	return {
		requestDetails: data?.data,
		isLoading,
		isError,
		error,
	}
}

export const useShareCorporateData = () => {
	const corporateDataApi = useAsyncApi(CorporateDataControllerApiFactory)
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationFn: async ({
			reference,
			answeredAllocationData,
		}: {
			reference: string
			answeredAllocationData?: CorporateAllocationData
		}) => {
			const requestBody = {
				answeredAllocationData,
			}
			const result = await corporateDataApi(api =>
				api.shareCorporateData(reference, requestBody)
			)
			return result
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QueryKey.GetCorporateDataRequests],
			})
		},
	})

	return mutation
}

interface PrefillProgress {
	stage: string
	message: string
}

export const usePrefillCorporateData = (
	onComplete?: (data: CorporateYearData) => void
) => {
	const dataFederationApiUrl = useDataFederationApiUrl()
	const [isPending, setIsPending] = React.useState(false)
	const [progress, setProgress] = React.useState<PrefillProgress | undefined>(
		undefined
	)
	const [error, setError] = React.useState<string | undefined>(undefined)
	const abortControllerRef = React.useRef<AbortController | undefined>(
		undefined
	)

	const prefillFromAttachments = React.useCallback(
		async ({
			year,
			attachmentIds,
		}: {
			year: number
			attachmentIds: readonly string[]
		}) => {
			setIsPending(true)
			setProgress(undefined)
			setError(undefined)

			// Create abort controller for this request
			abortControllerRef.current = new AbortController()

			try {
				const accessToken = authService.getAccessToken()
				if (!accessToken) {
					throw new Error('No access token available')
				}

				const url = new URL(`${dataFederationApiUrl}api/corporate-data/prefill`)

				const response = await fetch(url.toString(), {
					method: 'POST',
					headers: {
						Accept: 'text/event-stream',
						'Content-Type': 'application/json',
						Authorization: `Bearer ${accessToken}`,
					},
					body: JSON.stringify({
						year,
						attachmentIds,
					}),
					signal: abortControllerRef.current.signal,
				})

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`)
				}

				// TODO(larryhudson): Consider using EventSource to clean this up

				const reader = response.body?.getReader()
				const decoder = new TextDecoder()

				if (!reader) {
					throw new Error('Response body is not readable')
				}

				let buffer = ''
				let lastPrefillData: CorporateYearData | undefined

				while (true) {
					const { done, value } = await reader.read()

					if (done) {
						// Call the completion callback with the latest prefilled data
						if (lastPrefillData && onComplete) {
							onComplete(lastPrefillData)
						}

						setIsPending(false)
						setProgress(undefined)
						break
					}

					// Decode the chunk and add to buffer
					buffer += decoder.decode(value, { stream: true })

					// Process complete SSE messages
					const lines = buffer.split('\n\n')
					buffer = lines.pop() || '' // Keep incomplete message in buffer

					for (const line of lines) {
						if (line.trim() === '') {
							continue
						}

						// Parse SSE format
						const idMatch = line.match(/^id:(.+)$/m)
						const eventMatch = line.match(/^event:(.+)$/m)
						const dataMatch = line.match(/^data:(.+)$/m)

						if (eventMatch && dataMatch) {
							const eventName = eventMatch[1]
							const eventId = idMatch ? idMatch[1].trim() : undefined
							const eventData = JSON.parse(dataMatch[1])

							if (eventName === 'progress') {
								// Extract stage name from event ID (e.g., "PAGE_EXTRACTION_start" -> "PAGE_EXTRACTION")
								const stageName = eventId
									? eventId.replace(/_start$|_complete$/, '')
									: eventData.stage

								setProgress({
									stage: stageName,
									message: eventData.message,
								})
								// Store the latest prefill data
								lastPrefillData = eventData.data
								// eslint-disable-next-line no-console
								console.log('New corporate data from prefill', eventData.data)
							} else if (eventName === 'error') {
								setError(eventData.message)
								throw new Error(eventData.message)
							}
						}
					}
				}
			} catch (err) {
				setIsPending(false)
				if (err instanceof Error) {
					if (err.name === 'AbortError') {
						setError('Request was cancelled')
					} else {
						setError(err.message)
					}
					throw err
				}
				throw new Error('Unknown error occurred', { cause: err })
			} finally {
				abortControllerRef.current = undefined
				setIsPending(false)
			}
		},
		[dataFederationApiUrl, onComplete]
	)

	const cancel = React.useCallback(() => {
		abortControllerRef.current?.abort()
		setIsPending(false)
		setProgress(undefined)
	}, [])

	return {
		mutateAsync: prefillFromAttachments,
		isPending,
		progress,
		error,
		cancel,
	}
}
